import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('mobility_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global 401 handler: token invalid/expired → clear session and redirect to login.
// Avoids confusing "Invalid token" toasts and is the standard auth UX pattern.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const path = window.location.pathname;
      const wasLoginCall = error?.config?.url?.includes('/auth/login');
      // Don't redirect if it's a fresh login failure (wrong password) — let the page show the error.
      // Don't redirect if user is already on /login or /signup.
      const onAuthPage = path === '/login' || path === '/signup' || path === '/';
      if (!wasLoginCall && !onAuthPage) {
        localStorage.removeItem('mobility_token');
        localStorage.removeItem('mobility_user');
        // Use replace to avoid back-button loop into the broken state
        window.location.replace('/login?session=expired');
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me'),
};

// Cars API
export const carsAPI = {
  getAll: (params) => api.get('/cars', { params }),
  getMine: () => api.get('/cars/mine'),
  getById: (id) => api.get(`/cars/${id}`),
  create: (carData) => api.post('/cars', carData),
  update: (id, carData) => api.put(`/cars/${id}`, carData),
  delete: (id) => api.delete(`/cars/${id}`),
};

// Bookings API
export const bookingsAPI = {
  getAll: (params) => api.get('/bookings', { params }),
  getById: (id) => api.get(`/bookings/${id}`),
  create: (bookingData) => api.post('/bookings', bookingData),
  approve: (id) => api.put(`/bookings/${id}/approve`),
  reject: (id, reason) => api.put(`/bookings/${id}/reject`, { reason }),
  downloadAgreement: (id) => api.get(`/bookings/${id}/agreement.pdf`, { responseType: 'blob' }),
  createAgreementShareLink: (id) => api.post(`/bookings/${id}/agreement/share-link`),
};

// Reviews API
export const reviewsAPI = {
  create: (reviewData) => api.post('/reviews', reviewData),
  getCarReviews: (carId) => api.get(`/reviews/car/${carId}`),
};

// Messages API
export const messagesAPI = {
  create: (messageData) => api.post('/messages', messageData),
  getBookingMessages: (bookingId) => api.get(`/messages/booking/${bookingId}`),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getAnalytics: () => api.get('/admin/analytics'),
  banUser: (userId, payload) => api.patch(`/admin/users/${userId}/ban`, payload),
  verifyMobilitier: (mobilitierId, payload) => api.patch(`/admin/mobilitiers/${mobilitierId}/verify`, payload),
  getPendingPayouts: () => api.get('/admin/pending-payouts'),
  processPayout: (mobilitierId, payload) => api.post(`/admin/payout/${mobilitierId}`, payload),
  exportCSV: (resource) => api.get(`/admin/export/${resource}`, { responseType: 'blob' }),
  // Lists for management (use existing endpoints)
  getAllUsers: () => api.get('/admin/users'),
  getAllMobilitiers: () => api.get('/admin/mobilitiers'),
  getAllCars: () => api.get('/cars', { params: { limit: 200 } }),
  getAllBookings: () => api.get('/bookings'),
};

// Notifications
export const notificationsAPI = {
  list: (unread_only = false) => api.get('/notifications', { params: { unread_only } }),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Referrals
export const referralsAPI = {
  me: () => api.get('/referrals/me'),
  redeem: (code) => api.post('/referrals/redeem', { code }),
  mobilitierMe: () => api.get('/referrals/mobilitier/me'),
};

// Conversations
export const conversationsAPI = {
  list: () => api.get('/conversations'),
  markRead: (bookingId) => api.post(`/messages/booking/${bookingId}/read`),
};

// Reviews extra
export const reviewsExtraAPI = {
  forBooking: (bookingId) => api.get(`/reviews/booking/${bookingId}`),
};

// Users API
export const usersAPI = {
  getById: (id) => api.get(`/users/${id}`),
};

export default api;
